// Client-side attribution persistence.
//
// UTMs only exist in the URL of the LANDING page. Checkout (CartDrawer /
// HotSaleBanner → /api/checkout/simple → Shopify cart attributes →
// note_attributes → orders.utm_*) used to read window.location.search at
// click time, so attribution survived only when the buyer checked out from
// the exact page she landed on — verified in checkout_logs: almost every
// checkout arrived with no utm params. This module stores the last touch in
// localStorage at landing so any later checkout in the window can recover it.

export const ATTRIBUTION_KEY = 'olin_attribution'
export const ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000 // 30 days

export const ATTRIBUTION_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'fbclid',
] as const

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>

export type AttributionParams = Partial<Record<(typeof ATTRIBUTION_PARAMS)[number], string>>

/**
 * Extract attribution params from a query string. Returns null if none present.
 */
export function extractAttribution(search: string): AttributionParams | null {
  try {
    const qs = new URLSearchParams(search)
    const params: Record<string, string> = {}
    for (const k of ATTRIBUTION_PARAMS) {
      const v = qs.get(k)
      if (v) params[k] = v
    }
    return Object.keys(params).length ? params : null
  } catch {
    return null
  }
}

/**
 * Persist attribution params from a landing URL (last touch wins; a new touch
 * replaces the stored one entirely so params from different campaigns never mix).
 * No-op when the URL carries no attribution params.
 */
export function persistAttribution(
  search: string,
  storage: StorageLike | undefined = typeof window !== 'undefined' ? window.localStorage : undefined,
  now: number = Date.now()
): void {
  try {
    if (!storage) return
    const params = extractAttribution(search)
    if (!params) return
    storage.setItem(ATTRIBUTION_KEY, JSON.stringify({ params, ts: now }))
  } catch {
    // storage unavailable (private mode, quota) — attribution is best-effort
  }
}

/**
 * Read the stored last-touch attribution, or null if absent/expired/corrupt.
 */
export function readStoredAttribution(
  storage: StorageLike | undefined = typeof window !== 'undefined' ? window.localStorage : undefined,
  now: number = Date.now()
): AttributionParams | null {
  try {
    if (!storage) return null
    const raw = storage.getItem(ATTRIBUTION_KEY)
    if (!raw) return null
    const { params, ts } = JSON.parse(raw)
    if (typeof ts !== 'number' || now - ts > ATTRIBUTION_TTL_MS) return null
    if (!params || typeof params !== 'object') return null
    return params
  } catch {
    return null
  }
}

/**
 * Attribution to attach to a checkout: current-URL params take priority
 * (same-session ground truth), stored last touch fills the gap when the
 * buyer navigated since landing.
 */
export function getCheckoutAttribution(): AttributionParams {
  if (typeof window === 'undefined') return {}
  return extractAttribution(window.location.search) ?? readStoredAttribution() ?? {}
}
